import { useEffect, useState } from "react";

import { api, ApiError } from "../api/client";
import { ui } from "../lib/ui";
import type { NvrInfo } from "../types";
import { Panel } from "./Panel";

interface NvrPanelProps {
  branchId: string;
  nvr: NvrInfo | null;
  onSaved: (nvr: NvrInfo) => void;
}

/** The store's recorder: address, login and the folder downloads go to on
 *  this PC. Per PC like a linked camera folder; the password is write-only
 *  (the server says whether one is set, never what it is). */
export function NvrPanel({ branchId, nvr, onSaved }: NvrPanelProps) {
  const [host, setHost] = useState("");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [folder, setFolder] = useState("");
  const [https, setHttps] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setHost(nvr?.host ?? "");
    setUsername(nvr?.username ?? "");
    setPassword("");
    setFolder(nvr?.download_folder ?? "");
    setHttps(nvr?.use_https ?? false);
  }, [nvr]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setMessage(null);
    try {
      const saved = await api.saveNvr(branchId, {
        host: host.trim(),
        username: username.trim(),
        // Blank keeps the stored password.
        password: password === "" ? null : password,
        download_folder: folder.trim(),
        use_https: https,
      });
      setMessage("Recorder saved.");
      onSaved(saved);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Panel
      title="Recorder (NVR)"
      subtitle="The store's Hikvision recorder. Download fetches a day from it into <download folder>/<camera>/, which the run then reads in place."
    >
      {message && <p className={`${ui.notice} ${ui.noticeSuccess}`}>{message}</p>}
      {error && <p className={`${ui.notice} ${ui.noticeError}`}>{error}</p>}
      {nvr && !nvr.powershell && (
        <p className={`${ui.notice} ${ui.noticeError}`}>
          PowerShell was not found on this PC, so nothing can be downloaded.
        </p>
      )}
      <form
        className="flex flex-wrap items-end gap-3.5"
        onSubmit={(event) => {
          event.preventDefault();
          void save();
        }}
      >
        <label className={ui.field}>
          Address
          <input
            className={`${ui.input} w-40 font-mono text-xs`}
            value={host}
            placeholder="10.6.76.250"
            onChange={(e) => setHost(e.target.value)}
          />
        </label>
        <label className={ui.field}>
          Username
          <input
            className={`${ui.input} w-32`}
            value={username}
            autoComplete="off"
            onChange={(e) => setUsername(e.target.value)}
          />
        </label>
        <label className={ui.field}>
          Password
          <input
            className={`${ui.input} w-36`}
            type="password"
            value={password}
            autoComplete="new-password"
            placeholder={nvr?.has_password ? "•••••• (unchanged)" : ""}
            onChange={(e) => setPassword(e.target.value)}
          />
        </label>
        <label className={`${ui.field} min-w-[240px] flex-1`}>
          Download folder (on this PC)
          <input
            className={`${ui.input} font-mono text-xs`}
            value={folder}
            placeholder="D:\analytics"
            onChange={(e) => setFolder(e.target.value)}
          />
        </label>
        <label className="flex items-center gap-2 pb-2 text-[13px]">
          <input type="checkbox" checked={https} onChange={(e) => setHttps(e.target.checked)} />
          HTTPS
        </label>
        <button
          type="submit"
          className={ui.btn}
          disabled={busy || !host.trim() || !folder.trim()}
        >
          Save
        </button>
      </form>
      <p className="mt-3 text-xs text-muted">
        Each camera&apos;s NVR channel is set in the camera list above.
      </p>
    </Panel>
  );
}
